import { ResponseOrganization } from "@dashboard-v3/api";
import {
  Box,
  Grid,
  ListItem,
  ListItemText,
  TextField,
} from "@material-ui/core";
import ButtonWithLoading from "components/ButtonWithLoading";
import { useSnackbar } from "notistack";
import { ChangeEventHandler, KeyboardEventHandler, useState } from "react";
import { useTranslation } from "react-i18next";
import useOrganization from "utils/useOrganization";

export default function PasswordPolicy() {
  const { t } = useTranslation("storage");
  const { enqueueSnackbar } = useSnackbar();
  const { isLoading, organization, updateOrganization } = useOrganization();
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState<PasswordPolicyFrom>(initForm(organization));

  const onChange: ChangeEventHandler<HTMLInputElement> = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: Number(value) }));
  };

  const onKeyDown: KeyboardEventHandler<HTMLInputElement> = e => {
    const allowedChars = [
      "Backspace",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "End",
    ];

    if (e.ctrlKey || e.metaKey) {
      return;
    }

    if (allowedChars.includes(e.key)) {
      return;
    }

    if (Number.isNaN(parseInt(e.key))) {
      e.preventDefault();
    }
  };

  const onSubmit = async e => {
    e.preventDefault();

    const storagesPolicy = {
      passwordPolicy: { ...form },
    };

    setIsSaving(true);
    try {
      await updateOrganization({ storagesPolicy });
      enqueueSnackbar(t("Password policies saved successfully."), {
        variant: "success",
      });
    } catch (err) {
      enqueueSnackbar(
        t(
          "An error occurred while trying to save your password policy, please try again later."
        ),
        { variant: "error" }
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ListItem style={{ flexDirection: "column", alignItems: "stretch" }}>
      <ListItemText
        primary={t("passwordPolicy.title")}
        secondary={t("passwordPolicy.subtitle")}
        primaryTypographyProps={{ variant: "subtitle2" }}
        secondaryTypographyProps={{ variant: "caption" }}
      />
      <form onSubmit={onSubmit}>
        <Grid container spacing={4} style={{ maxWidth: "80%", marginTop: 5 }}>
          {fields.map(field => {
            return (
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  variant="outlined"
                  type="number"
                  name={field}
                  label={t(`passwordPolicy.form.${field}.label`)}
                  value={form[field]}
                  onChange={onChange}
                  onKeyDown={onKeyDown}
                  disabled={isLoading || isSaving}
                  inputProps={{ min: 0, max: 20 }}
                />
              </Grid>
            );
          })}
        </Grid>
        <Box marginTop="30px" textAlign="left" width="100%">
          <ButtonWithLoading
            type="submit"
            variant="contained"
            size="small"
            color="primary"
            disableElevation
            disabled={isLoading}
            loading={isSaving}
          >
            {t("common:save")}
          </ButtonWithLoading>
        </Box>
      </form>
    </ListItem>
  );
}

type Field = keyof ResponseOrganization["storagesPolicy"]["passwordPolicy"];
type PasswordPolicyFrom = Record<Field, number>;
const fields: Field[] = ["numbers", "symbols", "lowerCases", "upperCases"];

function initForm(org: ResponseOrganization) {
  const { passwordPolicy } = org.storagesPolicy;

  return () =>
    fields.reduce((acc, field) => {
      acc = { ...acc, [field]: passwordPolicy[field] || 0 };
      return acc;
    }, {} as PasswordPolicyFrom);
}
